import React, { useState, useEffect } from 'react';
import {
  Card,
  Title,
  Text,
  Tab,
  TabList,
  TabGroup,
  TabPanel,
  TabPanels,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Select,
  SelectItem,
  DateRangePicker,
  DateRangePickerValue,
  Button,
  Badge,
  Flex,
  Grid,
  Col,
  TextInput
} from '@tremor/react';
import {
  RiFileDownloadLine,
  RiSearchLine,
  RiRefreshLine
} from '@remixicon/react';
import { reportService } from '../../services/report';
import { useReportData } from '../../hooks/useReportData';
import { exportToExcel } from '../../utils/export';
import { 
  BradfordFactorEmployee, 
  AbsenceRecord,
  EmployeeLeaveBalance 
} from '../../types/report.types';

interface DetailedReportData {
  bradfordFactors: BradfordFactorEmployee[];
  absenceRecords: AbsenceRecord[];
  leaveBalances: EmployeeLeaveBalance[];
}

const DetailedReport: React.FC = () => {
  const reportDataHook = useReportData();
  
  // State for filters
  const [dateRange, setDateRange] = useState<DateRangePickerValue>({
    from: new Date(new Date().getFullYear(), 0, 1),
    to: new Date(),
  });
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [selectedEmployee, setSelectedEmployee] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // State for data
  const [detailedData, setDetailedData] = useState<DetailedReportData>({
    bradfordFactors: [],
    absenceRecords: [],
    leaveBalances: []
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch detailed report data
  const fetchDetailedData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [bradfordResp, trendsResp, leaveResp] = await Promise.allSettled([
        reportService.getBradfordFactorData({
          department: selectedDepartment !== 'all' ? selectedDepartment : undefined,
          employeeId: selectedEmployee !== 'all' ? selectedEmployee : undefined,
          startDate: dateRange.from?.toISOString().split('T')[0],
          endDate: dateRange.to?.toISOString().split('T')[0],
          includeAbsenceDetails: true
        }),
        reportService.getAbsenceTrendsData({
          startDate: dateRange.from?.toISOString().split('T')[0],
          endDate: dateRange.to?.toISOString().split('T')[0],
          department: selectedDepartment !== 'all' ? selectedDepartment : undefined,
          employeeId: selectedEmployee !== 'all' ? selectedEmployee : undefined,
          groupBy: 'employee',
          viewType: 'duration'
        }),
        reportService.getLeaveBalanceReport({
          startDate: dateRange.from?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0],
          endDate: dateRange.to?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0],
          department: selectedDepartment !== 'all' ? selectedDepartment : undefined,
          employeeId: selectedEmployee !== 'all' ? selectedEmployee : undefined,
        })
      ]);

      let newData: DetailedReportData = {
        bradfordFactors: [],
        absenceRecords: [],
        leaveBalances: []
      };

      if (bradfordResp.status === 'fulfilled' && bradfordResp.value.status === 'GENERATE_REPORT_SUCCESS') {
        newData.bradfordFactors = bradfordResp.value.payload.bradford_factor.bradfordFactors || [];
      }

      if (trendsResp.status === 'fulfilled' && trendsResp.value.status === 'ABSENCE_TRENDS_SUCCESS') {
        newData.absenceRecords = trendsResp.value.payload.absence_trends.absenceRecords || [];
      }

      if (leaveResp.status === 'fulfilled' && leaveResp.value.status === 'LEAVE_BALANCE_SUCCESS') {
        newData.leaveBalances = leaveResp.value.payload.leave_balance.employees || [];
      }

      setDetailedData(newData);
    } catch (err) {
      console.error('Error fetching detailed report data:', err);
      setError('Failed to load detailed report data');
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search term
  const filterBySearchTerm = <T extends { userName?: string; firstName?: string; lastName?: string }>(data: T[]): T[] => {
    if (!searchTerm) return data;
    
    return data.filter(item => {
      const fullName = item.userName || `${item.firstName} ${item.lastName}`;
      return fullName?.toLowerCase().includes(searchTerm.toLowerCase());
    });
  };

  // Export functions
  const exportBradfordData = () => {
    const filteredData = filterBySearchTerm(detailedData.bradfordFactors);
    const exportData = filteredData.map(emp => ({
      'Employee': emp.userName,
      'Department': emp.department,
      'Spells of Absence': emp.spellsOfAbsence,
      'Total Days Absent': emp.totalDaysAbsent,
      'Bradford Factor': emp.bradfordFactor,
      'Risk Level': emp.riskLevel
    }));
    
    exportToExcel(exportData, 'Detailed_Bradford_Factor_Report');
  };

  const exportAbsenceData = () => {
    const filteredData = filterBySearchTerm(detailedData.absenceRecords);
    const exportData = filteredData.map(record => ({
      'Employee': record.userName,
      'Department': record.department,
      'Role': record.role,
      'Location': record.location,
      'Absence Type': record.type,
      'Start Date': record.startDate,
      'End Date': record.endDate,
      'Duration (Days)': record.duration,
      'Reason': record.reason || 'N/A'
    }));
    
    exportToExcel(exportData, 'Detailed_Absence_Records');
  };

  const exportLeaveData = () => {
    const filteredData = filterBySearchTerm(detailedData.leaveBalances);
    const exportData = filteredData.flatMap(emp => 
      emp.balances.map(balance => ({
        'Employee': `${emp.firstName} ${emp.lastName}`,
        'Department': emp.department,
        'Role': emp.role,
        'Employment Type': emp.employmentType,
        'Location': emp.location,
        'Leave Type': balance.leaveType,
        'Entitled Days': balance.entitled,
        'Accrued Days': balance.accrued,
        'Used Days': balance.used,
        'Remaining Days': balance.remaining,
        'Carry Over Days': balance.carryOver
      }))
    );
    
    exportToExcel(exportData, 'Detailed_Leave_Balances');
  };

  // Load data on mount and filter changes
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchDetailedData();
  }, [dateRange, selectedDepartment, selectedEmployee]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Update employees when department changes
  useEffect(() => {
    if (selectedDepartment !== 'all') {
      reportDataHook.refetch.employees(selectedDepartment);
    }
  }, [selectedDepartment, reportDataHook.refetch]);

  const getRiskBadgeColor = (riskLevel: string) => {
    const colors: { [key: string]: 'green' | 'yellow' | 'orange' | 'red' } = {
      'Low': 'green',
      'Medium': 'yellow', 
      'High': 'orange',
      'Critical': 'red'
    };
    return colors[riskLevel] || 'gray';
  };

  return (
    <div className="p-4">
      <Flex justifyContent="between" alignItems="center" className="mb-6">
        <Title>Detailed HR Reports</Title>
        <Button
          size="sm"
          variant="secondary"
          icon={RiRefreshLine}
          onClick={fetchDetailedData}
          loading={loading}
        >
          Refresh Data
        </Button>
      </Flex>

      {error && (
        <Card className="mb-6 bg-red-50">
          <Text color="red">{error}</Text>
        </Card>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <Title>Filters</Title>
        <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4 mt-4">
          <Col>
            <Text className="mb-2">Date Range</Text>
            <DateRangePicker
              className="w-full"
              value={dateRange}
              onValueChange={setDateRange}
              enableSelect={false}
              selectPlaceholder="Select range"
            />
          </Col>
          
          <Col>
            <Text className="mb-2">Department</Text>
            <Select
              value={selectedDepartment}
              onValueChange={setSelectedDepartment}
              placeholder="Select Department"
              disabled={reportDataHook.loading.departments}
            >
              {reportDataHook.departments.map((dept) => (
                <SelectItem key={dept.id} value={dept.id}>
                  {dept.name}
                </SelectItem>
              ))}
            </Select>
          </Col>
          
          <Col>
            <Text className="mb-2">Employee</Text>
            <Select
              value={selectedEmployee}
              onValueChange={setSelectedEmployee}
              placeholder="Select Employee"
              disabled={reportDataHook.loading.employees}
            >
              {reportDataHook.employees.map((emp) => (
                <SelectItem key={emp.id} value={emp.id}>
                  {emp.name}
                </SelectItem>
              ))}
            </Select>
          </Col>
          
          <Col>
            <Text className="mb-2">Search</Text>
            <TextInput
              icon={RiSearchLine}
              placeholder="Search employees..."
              value={searchTerm}
              onValueChange={setSearchTerm}
            />
          </Col>
        </Grid>
      </Card>

      {loading ? (
        <Card className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <Text className="ml-4">Loading detailed reports...</Text>
        </Card>
      ) : (
        <TabGroup>
          <TabList className="mb-4">
            <Tab>Bradford Factor Details</Tab>
            <Tab>Absence Records</Tab>
            <Tab>Leave Balances</Tab>
          </TabList>
          
          <TabPanels>
            {/* Bradford Factor Details */}
            <TabPanel>
              <Card>
                <Flex justifyContent="between" alignItems="center" className="mb-4">
                  <Title>Bradford Factor Analysis</Title>
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={RiFileDownloadLine}
                    onClick={exportBradfordData}
                    disabled={detailedData.bradfordFactors.length === 0}
                  >
                    Export
                  </Button>
                </Flex>
                
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Employee</TableHeaderCell>
                      <TableHeaderCell>Department</TableHeaderCell>
                      <TableHeaderCell>Absence Spells</TableHeaderCell>
                      <TableHeaderCell>Total Days</TableHeaderCell>
                      <TableHeaderCell>Bradford Score</TableHeaderCell>
                      <TableHeaderCell>Risk Level</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filterBySearchTerm(detailedData.bradfordFactors).map((employee) => (
                      <TableRow key={employee.userId}>
                        <TableCell>{employee.userName}</TableCell>
                        <TableCell>{employee.department}</TableCell>
                        <TableCell className="text-center">{employee.spellsOfAbsence}</TableCell>
                        <TableCell className="text-center">{employee.totalDaysAbsent}</TableCell>
                        <TableCell className="text-center font-medium">{employee.bradfordFactor}</TableCell>
                        <TableCell>
                          <Badge color={getRiskBadgeColor(employee.riskLevel)}>
                            {employee.riskLevel}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {filterBySearchTerm(detailedData.bradfordFactors).length === 0 && (
                  <div className="text-center py-8 text-tremor-content">
                    No Bradford Factor data available for the selected criteria
                  </div>
                )}
              </Card>
            </TabPanel>

            {/* Absence Records */}
            <TabPanel>
              <Card>
                <Flex justifyContent="between" alignItems="center" className="mb-4">
                  <Title>Detailed Absence Records</Title>
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={RiFileDownloadLine}
                    onClick={exportAbsenceData}
                    disabled={detailedData.absenceRecords.length === 0}
                  >
                    Export
                  </Button>
                </Flex>
                
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Employee</TableHeaderCell>
                      <TableHeaderCell>Department</TableHeaderCell>
                      <TableHeaderCell>Type</TableHeaderCell>
                      <TableHeaderCell>Start Date</TableHeaderCell>
                      <TableHeaderCell>End Date</TableHeaderCell>
                      <TableHeaderCell>Duration</TableHeaderCell>
                      <TableHeaderCell>Reason</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filterBySearchTerm(detailedData.absenceRecords).map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>{record.userName}</TableCell>
                        <TableCell>{record.department}</TableCell>
                        <TableCell>
                          <Badge color="blue">
                            {record.type.split('_').map(word => 
                              word.charAt(0).toUpperCase() + word.slice(1)
                            ).join(' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(record.startDate).toLocaleDateString()}</TableCell>
                        <TableCell>{new Date(record.endDate).toLocaleDateString()}</TableCell>
                        <TableCell className="text-center">{record.duration} days</TableCell>
                        <TableCell>{record.reason || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {filterBySearchTerm(detailedData.absenceRecords).length === 0 && (
                  <div className="text-center py-8 text-tremor-content">
                    No absence records available for the selected criteria
                  </div>
                )}
              </Card>
            </TabPanel>

            {/* Leave Balances */}
            <TabPanel>
              <Card>
                <Flex justifyContent="between" alignItems="center" className="mb-4">
                  <Title>Employee Leave Balances</Title>
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={RiFileDownloadLine}
                    onClick={exportLeaveData}
                    disabled={detailedData.leaveBalances.length === 0}
                  >
                    Export
                  </Button>
                </Flex>
                
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Employee</TableHeaderCell>
                      <TableHeaderCell>Department</TableHeaderCell>
                      <TableHeaderCell>Leave Type</TableHeaderCell>
                      <TableHeaderCell>Entitled</TableHeaderCell>
                      <TableHeaderCell>Used</TableHeaderCell>
                      <TableHeaderCell>Remaining</TableHeaderCell>
                      <TableHeaderCell>Utilization</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filterBySearchTerm(detailedData.leaveBalances).flatMap((employee) =>
                      employee.balances.map((balance, index) => (
                        <TableRow key={`${employee.employeeId}-${balance.leaveType}`}>
                          <TableCell>
                            {index === 0 && `${employee.firstName} ${employee.lastName}`}
                          </TableCell>
                          <TableCell>
                            {index === 0 && employee.department}
                          </TableCell>
                          <TableCell>
                            <Badge color="green">
                              {balance.leaveType.split('_').map(word => 
                                word.charAt(0).toUpperCase() + word.slice(1)
                              ).join(' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">{balance.entitled}</TableCell>
                          <TableCell className="text-center">{balance.used}</TableCell>
                          <TableCell className="text-center">
                            <Badge color={balance.remaining <= 0 ? 'red' : balance.remaining < balance.entitled * 0.25 ? 'amber' : 'green'}>
                              {balance.remaining}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {balance.entitled > 0 ? `${((balance.used / balance.entitled) * 100).toFixed(1)}%` : '0%'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                
                {filterBySearchTerm(detailedData.leaveBalances).length === 0 && (
                  <div className="text-center py-8 text-tremor-content">
                    No leave balance data available for the selected criteria
                  </div>
                )}
              </Card>
            </TabPanel>
          </TabPanels>
        </TabGroup>
      )}
    </div>
  );
};

export default DetailedReport;